import {Injectable} from '@angular/core';
import {DbResponse} from "../../interceptors/db-response";
import {BehaviorSubject} from "rxjs";
import {IApiInterface} from "./api-interface";
import {TypeUtils} from "../../utils/type-utils";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import {Config} from "../../../config/config";
import {Utils} from "../../utils/utils";
import {filter, first, map} from "rxjs/operators";

@Injectable()
export class RequesterService {

  private _streamMap: { [id: string]: BehaviorSubject<DbResponse> };
  private _activeStreamMap: { [id: string]: boolean };

  constructor(
    private _httpClient: HttpClient,
  ) {
    this._streamMap = {};
    this._activeStreamMap = {};
  }

  public get auth(): IApiInterface {
    return this.apiInterface('auth');
  }

  public get core(): IApiInterface {
    return this.apiInterface('core');
  }

  public get manager(): IApiInterface {
    return this.apiInterface('manager');
  }

  public get backup(): IApiInterface {
    return this.apiInterface('backup');
  }

  public get optimizer(): IApiInterface {
    return this.apiInterface('optimizer');
  }

  public get seo(): IApiInterface {
    return this.apiInterface('seo');
  }

  public get performance(): IApiInterface {
    return this.apiInterface('performance');
  }

  public get optimizerDemo(): IApiInterface {
    return this.apiInterface('optimizerDemo');
  }

  public get security(): IApiInterface {
    return this.apiInterface('security');
  }

  public get notifications(): IApiInterface {
    return this.apiInterface('notifications');
  }

  public get wordpress(): IApiInterface {
    return this.apiInterface('wordpress');
  }

  public get public(): IApiInterface {
    return this.apiInterface('public');
  }
  public get miniProducts(): IApiInterface {
    return this.apiInterface('mini-products');
  }

  public get lxd(): IApiInterface {
    return this.apiInterface('lxd');
  }

  public get google(): IApiInterface {
    return this.apiInterface('google');
  }

  public get aiAssistant(): IApiInterface {
    return this.apiInterface('ai-assistant');
  }

  public get vibeCoding(): IApiInterface {
    return this.apiInterface('vibe-coding');
  }

  public get aiBuilder(): IApiInterface {
    return this.apiInterface('ai-builder');
  }

  public get woocom(): IApiInterface {
    return this.apiInterface('woocom');
  }

  public get tenWebWoocom(): IApiInterface {
    return this.apiInterface('tenWebWoocom');
  }

  public get tenWebStripe(): IApiInterface {
    return this.apiInterface('tenWebStripe');
  }
  public get woocomPayments(): IApiInterface {
    return this.apiInterface('woocomPayments');
  }
  public get woocomAnalytics(): IApiInterface {
    return this.apiInterface('woocomAnalytics');
  }

  public get miniTools(): IApiInterface {
    return this.apiInterface('mini-tools');
  }

  private apiInterface(api: string): IApiInterface {
    return <IApiInterface>{
      'get': (url, params: any, updateCache: boolean, isStream: boolean): BehaviorSubject<DbResponse> => {
        return this.request(api, 'get', url, params, null, updateCache, isStream);
      },
      'post': (url, params: any, data: any, updateCache: boolean, isStream: boolean): BehaviorSubject<DbResponse> => {
        return this.request(api, 'post', url, params, data, updateCache, isStream);
      },
      'put': (url, params: any, data: any, updateCache: boolean): BehaviorSubject<DbResponse> => {
        return this.request(api, 'put', url, params, data, updateCache, false);
      },
      'patch': (url, params: any, data: any, updateCache: boolean): BehaviorSubject<DbResponse> => {
        return this.request(api, 'patch', url, params, data, updateCache, false);
      },
      'delete': (url, params: any, data: any, updateCache: boolean): BehaviorSubject<DbResponse> => {
        return this.request(api, 'delete', url, params, data, updateCache, false);
      },
      'jsonp': (url, params: any, updateCache: boolean): BehaviorSubject<DbResponse> => {
        return this.request(api, 'jsonp', url, params, null, updateCache, false);
      },
    };
  }

  public request(api: string, method: string, url: string, params: any = null, data: any = null, updateCache: boolean = false, isStream: boolean = false): BehaviorSubject<DbResponse> {
    if (method !== 'get') {
      updateCache = true;
    }

    const streamId: string = [api, method, url, JSON.stringify(params), JSON.stringify(data)].join('.');
    if (!this._streamMap[streamId]) {
      this._streamMap[streamId] = new BehaviorSubject<DbResponse>(null);
    }
    const sourceStream$: BehaviorSubject<DbResponse> = this._streamMap[streamId];

    // TODO: improve this, write separate function to get original stream
    if (!this._activeStreamMap[streamId] && (updateCache || !sourceStream$.value || !sourceStream$.value.success)) {

      if (sourceStream$.value !== null) {
        sourceStream$.next(null);
      }

      this._activeStreamMap[streamId] = true;
      this.apiCall(api, method, url, params, data, isStream)
        .then((response: DbResponse): void => {
          sourceStream$.next(response);
          this._activeStreamMap && (this._activeStreamMap[streamId] = false);
        });
    }

    const clonedStream$: BehaviorSubject<DbResponse> = <BehaviorSubject<DbResponse>>(sourceStream$.pipe(map((response: DbResponse): DbResponse => TypeUtils.cloneDeep(response, isStream ? 1 : -1))));
    return clonedStream$;
  }

  private apiCall(api: string, method: string, url: string, params: any = null, data: any = null, isStream: boolean = false): Promise<DbResponse> {
    return new Promise((resolve: Function, reject: Function) => {
      // TODO: improve this
      if (api === 'wordpress' && method === 'jsonp') {
        const jsonpUrl: string = Config.wordpressEndpoint + url + '?' + Utils.stringifyUrlParams(params);
        this._httpClient.jsonp(jsonpUrl, 'callback')
            .pipe(
                filter((response: DbResponse): boolean => !!response),
                first())
            .subscribe((responseData: any): void => {
              const dbResponse: DbResponse = new DbResponse();
              dbResponse.success = true;
              dbResponse.data = responseData;
              resolve(dbResponse);
            },
            (errorResponse): void => {
              if (errorResponse instanceof HttpErrorResponse) {
                resolve(DbResponse.create(errorResponse));
              } else {
                console.error(errorResponse && errorResponse.message);
                resolve(DbResponse.create(errorResponse));
              }
            }
          );
      } else {
        /*data ? data['services[0]'] = 'hosted_domain' : '';
        data ? data['services[1]'] = 'cdn' : ''; */

        let httpRequest: HttpRequest<any> = this.getApiHttpRequest(api, method, url, params, data);

        if (isStream) {
          httpRequest = httpRequest.clone({
            responseType: 'blob'
          });
        }

        this._httpClient
          .request<DbResponse>(httpRequest)
          .pipe(first())
          .subscribe((response: HttpResponse<DbResponse>): void => {
            resolve(response);
          });
      }
    });
  }

  private getApiHttpRequest(api: string, method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    switch (api) {
      case 'auth':
        return this.getAuthRequest(method, url, params, data);

      case 'core':
        return this.getCoreRequest(method, url, params, data);

      case 'manager':
        return this.getManagerRequest(method, url, params, data);

      case 'backup':
        return this.getBackupRequest(method, url, params, data);

      case 'optimizer':
        return this.getOptimizerRequest(method, url, params, data);

      case 'seo':
        return this.getSeoRequest(method, url, params, data);

      case 'performance':
        return this.getPerformanceRequest(method, url, params, data);

      case 'optimizerDemo':
        return this.getOptimizerDemoRequest(method, url, params, data);

      case 'security':
        return this.getSecurityRequest(method, url, params, data);

      case 'notifications':
        return this.getNotificationsRequest(method, url, params, data);

      case 'wordpress':
        return this.getWordpressRequest(method, url, params, data);

      case 'public':
        return this.getPublicRequest(method, url, params, data);

      case 'mini-products':
        return this.getMiniProductsRequest(method, url, params, data);

      case 'mini-tools':
        return this.getMiniToolsRequest(method, url, params, data);

      case 'lxd':
        return this.getLxdRequest(method, url, params, data);

      case 'google':
        return this.getGoogleRequest(method, url, params, data);

      case 'ai-assistant':
        return this.getAiAssistantRequest(method, url, params, data);

      case 'ai-builder':
        return this.getAiBuilderRequest(method, url, params, data);

      case 'woocom':
        return this.getWoocomRequest(method, url, params, data);

      case 'tenWebWoocom':
        return this.getTenWebWoocomRequest(method, url, params, data);

      case 'tenWebStripe':
        return this.getTenWebStripeRequest(method, url, params, data);

      case 'woocomPayments':
        return this.getTenWebWoocomPaymentsRequest(method, url, params, data);

      case 'woocomAnalytics':
        return this.getWoocomAnalyticsRequest(method, url, params, data);

      case 'ai-builder':
        return this.getAiBuilderRequest(method, url, params, data);

        case 'vibe-coding':
            return this.getVibeCodingRequest(method, url, params, data);
    }
    return null;
  }

  private getCoreRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}core.v1+json`
    });
      const shouldSkipToken = url.includes('subscriptions?from_dashboard');
      const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
      if (userAccessToken && !shouldSkipToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}.${Config.serviceKey}`,
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    const _initialParams = {
      headers: httpHeaders,
      params: httpParams
    };
    if (!url.match(/download|invoices/)) {
      _initialParams['withCredentials'] = true;
    }
    return new HttpRequest(
      method,
      Config.coreEndpoint + url,
      data,
      _initialParams
    );
  }

  private getManagerRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}manager.v1+json`,
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.managerEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getBackupRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}backup.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.backupEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getOptimizerRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}optimizer.v3+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.optimizerEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getSeoRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}seo.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.seoEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getPerformanceRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}performance.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.performanceEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getOptimizerDemoRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}optimizerdemonstration.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.optimizerDemonstrationEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getSecurityRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}security.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.securityEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getNotificationsRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}notifications.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.notificationsEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getWordpressRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    const httpHeaders = new HttpHeaders();
    const httpParams: HttpParams = Utils.httpParamsFromObject(Object.assign({}, params, data));
    return new HttpRequest(
      method,
      Config.wordpressEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getPublicRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.publicEndpoint + url,
      data,
      {
        params: httpParams
      }
    );
  }
  private getMiniProductsRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.miniProductsEndpoint + url,
      data,
      {
        params: httpParams
      }
    );
  }

  private getMiniToolsRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    const headers = new HttpHeaders({
      'Content-Type': `application/json`,
      'Accept': '*/*',
    });
    return new HttpRequest(
      method,
      Config.miniToolsEndpoint + url,
      data,
      {
        headers,
        params: httpParams
      }
    );
  }

  private getLxdRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}lxd.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.lxdEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }


  public clearCache(): void {
    this._streamMap = {};
    this._activeStreamMap = {};
  }

  private getGoogleRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    const httpHeaders = new HttpHeaders();
    const httpParams: HttpParams = Utils.httpParamsFromObject(Object.assign({}, params, data));
    return new HttpRequest(
      method,
      Config.googleEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getAiAssistantRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}aiassistantapi.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }
    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    return new HttpRequest(
      method,
      Config.aiAssistantEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getWoocomRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}woocommerce.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }
    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    return new HttpRequest(
      method,
      Config.woocomEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getTenWebWoocomRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}woocommerce.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    let search = '';
    if (params && params.search) {
      search = `&search=${encodeURIComponent(params.search)}`;
      delete params.search;
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    return new HttpRequest(
      method,
      Config.tenWebWoocomEndpoint + url + search,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getTenWebStripeRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}woocommerce.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    let search = '';
    if (params && params.search) {
      search = `&search=${encodeURIComponent(params.search)}`;
      delete params.search;
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    return new HttpRequest(
      method,
      Config.tenWebStripeEndpoint + url + search,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }


  private getTenWebWoocomPaymentsRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}woocommerce.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    let search = '';
    if (params && params.search) {
      search = `&search=${encodeURIComponent(params.search)}`;
      delete params.search;
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    return new HttpRequest(
      method,
      Config.woocomPaymentsEndpoint + url + search,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getVibeCodingRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}wvc.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
          'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
        method,
        Config.vibeCodingEndpoint + url,
        data,
        {
          headers: httpHeaders,
          params: httpParams
        }
    );
  }

  private getWoocomAnalyticsRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}woocommerce.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
          'Authorization', `Bearer ${userAccessToken}`
      );
    }

    let search = '';
    if (params && params.search) {
      search = `&search=${encodeURIComponent(params.search)}`;
      delete params.search;
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    return new HttpRequest(
        method,
        Config.woocomAnalyticsEndPoint + url + search,
        data,
        {
          headers: httpHeaders,
          params: httpParams
        }
    );
  }

  private getAiBuilderRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}x.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }
    const httpParams: HttpParams = Utils.httpParamsFromObject(params);
    return new HttpRequest(
      method,
      Config.aiBuilderEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }

  private getAuthRequest(method: string, url: string, params: any = null, data: any = null): HttpRequest<any> {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}auth.v1+json`
    });
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (userAccessToken) {
      httpHeaders = httpHeaders.set(
        'Authorization', `Bearer ${userAccessToken}`
      );
    }

    const httpParams: HttpParams = Utils.httpParamsFromObject(params);

    return new HttpRequest(
      method,
      Config.authEndpoint + url,
      data,
      {
        headers: httpHeaders,
        params: httpParams
      }
    );
  }


}
